import { useEffect, useState } from "react";
import { Plus, Car, Trash2, Pencil, Download, ChevronDown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import "../css/Vehicles.css";

function VehicleCard({ vehicle, onDelete }) {
  const [selectedColor, setSelectedColor] = useState(
    vehicle.VehicleColors?.[0] || null
  );
  const [pricingOpen, setPricingOpen] = useState(false);
  const navigate = useNavigate();

  const formatPrice = (price) =>
    price != null ? `₹ ${Number(price).toLocaleString("en-IN")}` : "—";

  const categoryLabel = {
    "n-first":       "n-First",
    "diplos":        "Diplos",
    "institutional": "Institutional",
  }[vehicle.categoryName] ?? vehicle.categoryName;

  const statePrices = vehicle.VehicleStatePrices ?? [];

  return (
    <div className="vc-card">

      {/* Image */}
      <div className="vc-image-wrap">
        <span className="vc-category-badge">{categoryLabel}</span>
        {selectedColor?.imageUrl ? (
          <img
            src={selectedColor.imageUrl}
            alt={vehicle.variantName}
            className="vc-image"
            loading="lazy"
          />
        ) : (
          <div className="vc-no-image"><Car size={36} /></div>
        )}
      </div>

      {/* Body */}
      <div className="vc-body">

        {/* Name */}
        <h3 className="vc-name">{vehicle.variantName}</h3>

        {/* Colors */}
        {vehicle.VehicleColors?.length > 0 && (
          <div className="vc-colors">
            {vehicle.VehicleColors.map((c) => (
              <button
                key={c.id}
                className={`vc-color-dot ${selectedColor?.id === c.id ? "vc-color-active" : ""}`}
                style={{ background: c.hex }}
                onClick={() => setSelectedColor(c)}
                title={c.name}
              />
            ))}
            <span className="vc-color-name">{selectedColor?.name}</span>
          </div>
        )}

        {/* Metrics strip */}
        {/* {vehicle.VehicleMetrics?.length > 0 && (
          <div className="vc-metrics">
            {vehicle.VehicleMetrics.map((m, i) => (
              <div key={i} className="vc-metric">
                <strong>{m.value}</strong>
                <small>{m.label}</small>
              </div>
            ))}
          </div>
        )} */}

        {/* Specifications */}
        {vehicle.VehicleDetails?.length > 0 && (
          <div className="vc-details">
            {vehicle.VehicleDetails.map((d, i) => (
              <div key={i} className="vc-detail-row">
                <span className="vc-detail-label">{d.label}</span>
                <span className="vc-detail-value">{d.value}</span>
              </div>
            ))}
          </div>
        )}

        {/* State-wise Pricing */}
        {statePrices.length > 0 && (
          <div className="vc-pricing">
            <button
              className="vc-pricing-toggle"
              onClick={() => setPricingOpen((o) => !o)}
            >
              <span>State-wise Pricing</span>
              <span className="vc-pricing-meta">
                {statePrices.length} state{statePrices.length !== 1 ? "s" : ""}
              </span>
              <ChevronDown
                size={14}
                className={`vc-chevron ${pricingOpen ? "vc-chevron-open" : ""}`}
              />
            </button>

            {pricingOpen && (
              <div className="vc-pricing-table">
                <div className="vc-pricing-head">
                  <span>State</span>
                  <span>Ex-Showroom</span>
                  <span>On-Road</span>
                </div>
                {statePrices.map((sp, i) => (
                  <div key={i} className="vc-pricing-row">
                    <span className="vc-pricing-state">
                      {sp.State?.name ?? `State ${sp.state_id}`}
                      {sp.State?.state_code && (
                        <em className="vc-state-code">{sp.State.state_code}</em>
                      )}
                    </span>
                    <span>{formatPrice(sp.ex_showroom_price)}</span>
                    <span>{formatPrice(sp.on_road_price)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Brochure */}
        {vehicle.VehicleBrochure?.pdfUrl && (
          <a
            href={vehicle.VehicleBrochure.pdfUrl}
            target="_blank"
            rel="noreferrer"
            className="vc-brochure"
          >
            <Download size={13} /> Download Brochure
          </a>
        )}

      </div>

      {/* Actions */}
      <div className="vc-actions">
        <button
          className="vc-btn vc-btn-edit"
          onClick={() => navigate(`/edit-vehicle/${vehicle.id}`)}
        >
          <Pencil size={14} /> Edit
        </button>
        <button
          className="vc-btn vc-btn-delete"
          onClick={() => onDelete(vehicle.id)}
        >
          <Trash2 size={14} /> Delete
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

const CATEGORIES  = ["all", "n-first", "diplos", "institutional"];
const CAT_LABELS  = { all: "All", "n-first": "n-First", diplos: "Diplos", institutional: "Institutional" };

export default function Vehicles() {
  const [vehicles, setVehicles]             = useState([]);
  const [activeCategory, setActiveCategory] = useState("all");
  const [loading, setLoading]               = useState(true);
  const navigate = useNavigate();

  const fetchVehicles = async() => {
    try {
    setLoading(true);
   
    const response = await fetch(
      "/api/vehicles"
    );

    const result = await response.json();

    console.log("Vehicles:", result.data);

    setVehicles(result.data || []);
  } catch (error) {
    console.error("Error fetching vehicles:", error);
  } finally {
    setLoading(false);
  }
      
  };

  useEffect(() => { fetchVehicles(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this vehicle? This cannot be undone.")) return;
    await fetch(`/api/vehicles/${id}`, { method: "DELETE" });
    fetchVehicles();
  };

  const filtered =
    activeCategory === "all"
      ? vehicles
      : vehicles.filter((v) => v.categoryName === activeCategory);

  return (
    <div className="vp-page">

      {/* Header */}
      <div className="vp-header">
        <div className="vp-header-left">
          <div className="vp-header-icon"><Car size={20} /></div>
          <div>
            <h1 className="vp-title">Vehicles</h1>
            <p className="vp-subtitle">
              {vehicles.length} variant{vehicles.length !== 1 ? "s" : ""} listed
            </p>
          </div>
        </div>
        <button className="vp-add-btn" onClick={() => navigate("/add-vehicle")}>
          <Plus size={16} /> Add Vehicle
        </button>
      </div>

      {/* Category tabs */}
      <div className="vp-tabs">
        {CATEGORIES.map((cat) => (
          <button
            key={cat}
            className={`vp-tab ${activeCategory === cat ? "vp-tab-active" : ""}`}
            onClick={() => setActiveCategory(cat)}
          >
            {CAT_LABELS[cat]}
            <span className="vp-tab-count">
              {cat === "all"
                ? vehicles.length
                : vehicles.filter((v) => v.categoryName === cat).length}
            </span>
          </button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <div className="vp-loading">
          <div className="vp-spinner" />
          <p>Loading vehicles…</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="vp-empty">
          <Car size={44} strokeWidth={1.2} />
          <p>No vehicles in this category</p>
          <button className="vp-add-btn" onClick={() => navigate("/add-vehicle")}>
            <Plus size={15} /> Add one now
          </button>
        </div>
      ) : (
        <div className="vp-grid">
          {filtered.map((vehicle) => (
            <VehicleCard key={vehicle.id} vehicle={vehicle} onDelete={handleDelete} />
          ))}
        </div>
      )}

    </div>
  );
}


